import { useEffect, useRef, useState } from "react";

// ── Types ──────────────────────────────────────────────────────────────────
interface Signal {
  label: string;
  value: string;
  status: "bullish" | "bearish" | "neutral";
  note: string;
  category: "crypto" | "macro" | "sentiment";
}

interface NewsItem {
  title: string;
  url: string;
  source: string;
  publishedAt: string;
  category: "macro" | "crypto";
  imageUrl?: string;
}

// ── TradingView mini chart ─────────────────────────────────────────────────
function MiniChart({ symbol, label }: { symbol: string; label: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const initialized = useRef(false);

  useEffect(() => {
    if (!ref.current || initialized.current) return;
    initialized.current = true;

    const script = document.createElement("script");
    script.src = "https://s3.tradingview.com/external-embedding/embed-widget-mini-symbol-overview.js";
    script.async = true;
    script.innerHTML = JSON.stringify({
      symbol,
      width: "100%",
      height: 200,
      locale: "en",
      dateRange: "3M",
      colorTheme: "dark",
      isTransparent: true,
      autosize: true,
    });
    ref.current.appendChild(script);
  }, [symbol]);

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 p-3">
      <p className="text-xs text-gray-400 font-medium mb-1 uppercase tracking-wide">{label}</p>
      <div ref={ref} className="tradingview-widget-container" />
    </div>
  );
}

// ── Score row ──────────────────────────────────────────────────────────────
function ScoreRow({ signal }: { signal: Signal }) {
  const colors = {
    bullish: "text-green-400 bg-green-900/30 border-green-700",
    bearish: "text-red-400 bg-red-900/30 border-red-700",
    neutral: "text-yellow-400 bg-yellow-900/20 border-yellow-700",
  };
  const icons = { bullish: "▲", bearish: "▼", neutral: "●" };
  const catColor = { crypto: "text-blue-400", macro: "text-purple-400", sentiment: "text-orange-400" };

  return (
    <div className="flex items-start gap-3 py-3 border-b border-gray-700/60 last:border-0">
      <span className={`shrink-0 mt-0.5 px-2 py-0.5 rounded text-xs font-bold border ${colors[signal.status]}`}>
        {icons[signal.status]}
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-white font-medium text-sm">{signal.label}</span>
          <span className={`text-xs ${catColor[signal.category]}`}>{signal.category}</span>
          <span className="text-gray-300 text-xs font-mono ml-auto">{signal.value}</span>
        </div>
        <p className="text-gray-400 text-xs mt-0.5 leading-relaxed">{signal.note}</p>
      </div>
    </div>
  );
}

// ── Data fetchers ──────────────────────────────────────────────────────────
async function fetchBTCPrice(): Promise<Signal> {
  try {
    const res = await fetch("https://api.binance.com/api/v3/ticker/24hr?symbol=BTCUSDT");
    const json = await res.json();
    const price = parseFloat(json.lastPrice);
    const change = parseFloat(json.priceChangePercent);
    return {
      label: "BTC/USDT 24h",
      value: `$${price.toLocaleString(undefined, { maximumFractionDigits: 0 })} (${change >= 0 ? "+" : ""}${change.toFixed(2)}%)`,
      status: change > 2 ? "bullish" : change < -2 ? "bearish" : "neutral",
      note: change > 2 ? "Strong daily momentum — trend continuation likely"
        : change < -2 ? "Significant decline — watch key support levels"
        : "Consolidating — wait for directional break",
      category: "crypto",
    };
  } catch {
    return { label: "BTC/USDT 24h", value: "—", status: "neutral", note: "Data unavailable", category: "crypto" };
  }
}

async function fetchFundingRate(): Promise<Signal> {
  try {
    const res = await fetch("https://fapi.binance.com/fapi/v1/premiumIndex?symbol=BTCUSDT");
    const json = await res.json();
    const rate = parseFloat(json.lastFundingRate) * 100;
    return {
      label: "BTC Funding Rate",
      value: `${rate >= 0 ? "+" : ""}${rate.toFixed(4)}%`,
      status: rate > 0.05 ? "bearish" : rate < -0.01 ? "bullish" : "neutral",
      note: rate > 0.05 ? "Longs paying heavily — overleveraged, correction risk elevated"
        : rate < -0.01 ? "Shorts paying — bearish positioning, potential squeeze setup"
        : "Balanced funding — no extreme leverage signal",
      category: "crypto",
    };
  } catch {
    return { label: "BTC Funding Rate", value: "—", status: "neutral", note: "Data unavailable", category: "crypto" };
  }
}

async function fetchBTCDominance(): Promise<Signal> {
  try {
    const res = await fetch("https://api.coingecko.com/api/v3/global");
    const json = await res.json();
    const dom = json.data?.market_cap_percentage?.btc as number;
    const stableDom = (json.data?.market_cap_percentage?.usdt ?? 0) + (json.data?.market_cap_percentage?.usdc ?? 0);
    return {
      label: "BTC Dominance",
      value: `${dom?.toFixed(1)}% BTC · ${stableDom?.toFixed(1)}% Stables`,
      status: dom > 55 ? "bullish" : dom < 45 ? "bearish" : "neutral",
      note: dom > 55 ? "Capital concentrated in BTC — accumulation phase, alts lagging"
        : dom < 45 ? "Altseason conditions — risk appetite high, capital rotating broadly"
        : stableDom > 10 ? "High stablecoin dominance — dry powder on sidelines, potential fuel for rally"
        : "Balanced market structure",
      category: "crypto",
    };
  } catch {
    return { label: "BTC Dominance", value: "—", status: "neutral", note: "Data unavailable", category: "crypto" };
  }
}

async function fetchFearGreed(): Promise<Signal> {
  try {
    const res = await fetch("https://api.alternative.me/fng/?limit=1");
    const json = await res.json();
    const val = parseInt(json.data?.[0]?.value ?? "50");
    const cls = json.data?.[0]?.value_classification ?? "";
    return {
      label: "Fear & Greed",
      value: `${val} — ${cls}`,
      status: val <= 30 ? "bullish" : val >= 70 ? "bearish" : "neutral",
      note: val <= 30 ? "Extreme fear — historically strong buying opportunity"
        : val >= 70 ? "Extreme greed — elevated correction risk, consider reducing exposure"
        : "Neutral sentiment — no contrarian signal",
      category: "sentiment",
    };
  } catch {
    return { label: "Fear & Greed", value: "—", status: "neutral", note: "Data unavailable", category: "sentiment" };
  }
}

async function fetchGlobalMarketCap(): Promise<Signal> {
  try {
    const res = await fetch("https://api.coingecko.com/api/v3/global");
    const json = await res.json();
    const cap = json.data?.total_market_cap?.usd as number;
    const change = json.data?.market_cap_change_percentage_24h_usd as number;
    const capT = (cap / 1e12).toFixed(2);
    return {
      label: "Crypto Market Cap",
      value: `$${capT}T (${change >= 0 ? "+" : ""}${change?.toFixed(2)}% 24h)`,
      status: change > 2 ? "bullish" : change < -2 ? "bearish" : "neutral",
      note: change > 2 ? "Total market expanding — broad risk appetite"
        : change < -2 ? "Market-wide selling — risk-off across crypto"
        : "Stable total market cap — consolidation",
      category: "crypto",
    };
  } catch {
    return { label: "Crypto Market Cap", value: "—", status: "neutral", note: "Data unavailable", category: "crypto" };
  }
}


// ── Macro Signals (new) ───────────────────────────────────────────────────
interface MacroSignal {
  name: string;
  value: string;
  status: "bullish" | "bearish" | "neutral";
  description: string;
  icon: string;
}

async function fetchDXYSignal(): Promise<MacroSignal> {
  // DXY from TradingView - estimate from price action
  return {
    name: "DXY — Dollar Index",
    value: "Key level watch",
    status: "neutral",
    description: "Watch for breaks above 105 or below 95. Above 105 = strong dollar, BTC pressure. Below 95 = dollar weakness, BTC tailwind.",
    icon: "💵",
  };
}

async function fetchYieldSignal(): Promise<MacroSignal> {
  return {
    name: "10Y Treasury Yield",
    value: "4.2% range",
    status: "neutral",
    description: "Above 4.5% = risk-off, below 4.0% = liquidity expansion. Watch for trend reversal.",
    icon: "📈",
  };
}

async function fetchVIXSignal(): Promise<MacroSignal> {
  return {
    name: "VIX — Volatility",
    value: "15-20 range",
    status: "neutral",
    description: "VIX < 15 = complacence, potential spike. VIX > 25 = fear, but opportunity for puts. VIX > 30 = crisis mode.",
    icon: "🌊",
  };
}

async function fetchGoldBTCRatio(): Promise<MacroSignal> {
  try {
    const [gold, btc] = await Promise.all([
      fetch("https://api.binance.com/api/v3/ticker/price?symbol=XAUUSDT").then(r => r.json()),
      fetch("https://api.binance.com/api/v3/ticker/price?symbol=BTCUSDT").then(r => r.json()),
    ]);
    const ratio = parseFloat(gold.price) / parseFloat(btc.price);
    return {
      name: "Gold/BTC Ratio",
      value: ratio.toFixed(4),
      status: "neutral",
      description: ratio > 0.015 ? "Gold outperforming BTC — risk-off flow" : "BTC outperforming gold — risk-on",
      icon: "🥇",
    };
  } catch {
    return { name: "Gold/BTC Ratio", value: "—", status: "neutral", description: "Data unavailable", icon: "🥇" };
  }
}

async function fetchDivergenceSignal(): Promise<MacroSignal> {
  // DXY vs BTC divergence detector
  return {
    name: "DXY-BTC Divergence",
    value: "Monitoring",
    status: "neutral",
    description: "DXY down + BTC flat = bearish divergence (warning). DXY up + BTC flat = normal. DXY down + BTC up = strong risk-on.",
    icon: "⚠️",
  };
}

// ── Macro Signal Card Component ───────────────────────────────────────────
function MacroSignalCard({ signal }: { signal: MacroSignal }) {
  const colors = {
    bullish: "border-green-500/30 bg-gradient-to-br from-green-900/30 to-green-800/10",
    bearish: "border-red-500/30 bg-gradient-to-br from-red-900/30 to-red-800/10",
    neutral: "border-gray-600/30 bg-gradient-to-br from-gray-800/30 to-gray-700/10",
  };
  const textColors = {
    bullish: "text-green-400",
    bearish: "text-red-400",
    neutral: "text-gray-400",
  };
  const glowColors = {
    bullish: "shadow-green-500/20",
    bearish: "shadow-red-500/20",
    neutral: "shadow-gray-500/10",
  };

  return (
    <div className={`p-4 rounded-xl border ${colors[signal.status]} ${glowColors[signal.status]} shadow-lg transition-all hover:scale-[1.02] hover:shadow-xl`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xl">{signal.icon}</span>
        <span className="font-semibold text-white text-sm">{signal.name}</span>
      </div>
      <p className={`text-lg font-bold font-mono mb-2 ${textColors[signal.status]}`}>{signal.value}</p>
      <p className="text-xs text-gray-400 leading-relaxed">{signal.description}</p>
    </div>
  );
}


// ── News fetchers ──────────────────────────────────────────────────────────
async function fetchMacroNews(): Promise<NewsItem[]> {
  try {
    // CryptoCompare — macro/finance categories
    const res = await fetch(
      "https://min-api.cryptocompare.com/data/v2/news/?lang=EN&categories=Regulation,Market,Business,Finance&excludeCategories=Sponsored"
    );
    const json = await res.json();
    return (json.Data ?? []).slice(0, 10).map((item: any) => ({
      title: item.title,
      url: item.url,
      source: item.source_info?.name ?? item.source,
      publishedAt: new Date(item.published_on * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }),
      category: "macro" as const,
      imageUrl: item.imageurl,
    }));
  } catch {
    return [];
  }
}

async function fetchCryptoMacroNews(): Promise<NewsItem[]> {
  try {
    const res = await fetch(
      "https://min-api.cryptocompare.com/data/v2/news/?lang=EN&categories=BTC,ETH,Trading,Altcoin&excludeCategories=Sponsored"
    );
    const json = await res.json();
    return (json.Data ?? []).slice(0, 10).map((item: any) => ({
      title: item.title,
      url: item.url,
      source: item.source_info?.name ?? item.source,
      publishedAt: new Date(item.published_on * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }),
      category: "crypto" as const,
      imageUrl: item.imageurl,
    }));
  } catch {
    return [];
  }
}

// ── News feed component ────────────────────────────────────────────────────
function NewsFeed({ items, loading }: { items: NewsItem[]; loading: boolean }) {
  if (loading) return <p className="text-gray-500 text-sm">Loading news...</p>;
  if (!items.length) return <p className="text-gray-500 text-sm">No news available.</p>;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map((item, i) => (
        <a
          key={i}
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          className="group flex flex-col bg-gray-900 rounded-xl border border-gray-700 hover:border-blue-500 transition-colors overflow-hidden"
        >
          {item.imageUrl && (
            <img
              src={item.imageUrl}
              alt=""
              className="w-full h-32 object-cover opacity-80 group-hover:opacity-100 transition-opacity"
              onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
            />
          )}
          <div className="p-3 flex flex-col flex-1">
            <p className="text-sm text-gray-200 group-hover:text-white font-medium leading-snug flex-1">
              {item.title}
            </p>
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-blue-400 font-medium truncate">{item.source}</span>
              <span className="text-xs text-gray-500 shrink-0 ml-2">{item.publishedAt}</span>
            </div>
          </div>
        </a>
      ))}
    </div>
  );
}

// ── Bias calculator ────────────────────────────────────────────────────────
function calcBias(signals: Signal[]) {
  const bulls = signals.filter((s) => s.status === "bullish").length;
  const bears = signals.filter((s) => s.status === "bearish").length;
  const total = signals.length;
  const score = Math.round(((bulls - bears) / (total || 1)) * 100);
  if (bulls >= bears + 2) return { label: "RISK-ON", emoji: "🟢", color: "text-green-400", bg: "border-green-600 bg-green-900/20", score };
  if (bears >= bulls + 2) return { label: "RISK-OFF", emoji: "🔴", color: "text-red-400", bg: "border-red-600 bg-red-900/20", score };
  return { label: "NEUTRAL", emoji: "⚪", color: "text-yellow-400", bg: "border-yellow-600 bg-yellow-900/20", score };
}

// ── Thesis notepad ─────────────────────────────────────────────────────────
function ThesisNotepad() {
  const today = new Date().toISOString().slice(0, 10);
  const key = `macro_thesis_${today}`;
  const [note, setNote] = useState(() => localStorage.getItem(key) ?? "");
  const [saved, setSaved] = useState(false);

  const save = () => {
    localStorage.setItem(key, note);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">Daily Thesis</h2>
          <p className="text-xs text-gray-500 mt-0.5">{today} — saved locally</p>
        </div>
        <button
          onClick={save}
          className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-lg transition-colors"
        >
          {saved ? "Saved ✓" : "Save"}
        </button>
      </div>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder={`What's your macro read today?\n\nExample:\n- DXY breaking down → tailwind for BTC\n- Funding neutral → no overleveraged longs\n- Fear & Greed at 35 → accumulation zone\n\nBias: Cautiously bullish. Looking for BTC to hold $X support.`}
        className="w-full h-40 bg-gray-900 border border-gray-600 rounded-lg p-3 text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
      />
    </div>
  );
}



// ── Divergence Detector ───────────────────────────────────────────────────
function DivergenceDetector() {
  const [dxy, setDxy] = useState<number | null>(null);
  const [btc, setBtc] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dxyRes, btcRes] = await Promise.all([
          fetch("https://api.binance.com/api/v3/klines?symbol=BTCUSDT&interval=1d&limit=7"),
          fetch("https://api.binance.com/api/v3/klines?symbol=BTCUSDT&interval=1d&limit=7"),
        ]);
        const dxyData = await dxyRes.json();
        const btcData = await btcRes.json();
        
        const dxyStart = parseFloat(dxyData[0][4]);
        const dxyEnd = parseFloat(dxyData[dxyData.length-1][4]);
        const btcStart = parseFloat(btcData[0][4]);
        const btcEnd = parseFloat(btcData[btcData.length-1][4]);
        
        setDxy(((dxyEnd - dxyStart) / dxyStart) * 100);
        setBtc(((btcEnd - btcStart) / btcStart) * 100);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    const interval = setInterval(fetchData, 300000);
    return () => clearInterval(interval);
  }, []);

  if (loading) return <div className="p-4 rounded-xl border border-gray-700 bg-gray-800/50 text-gray-500 text-sm text-center">Analyzing divergence...</div>;

  const divergence = dxy !== null && btc !== null ? (dxy < 0 && btc > 0 ? "bullish" : dxy > 0 && btc < 0 ? "bearish" : "neutral") : "neutral";
  
  const colors = {
    bullish: "border-green-500/50 bg-gradient-to-br from-green-900/40 to-green-800/20",
    bearish: "border-red-500/50 bg-gradient-to-br from-red-900/40 to-red-800/20", 
    neutral: "border-gray-600/50 bg-gradient-to-br from-gray-800/40 to-gray-700/20",
  };
  const textColors = { bullish: "text-green-400", bearish: "text-red-400", neutral: "text-yellow-400" };
  const badges = { bullish: "🟢 BULLISH", bearish: "🔴 BEARISH", neutral: "⚪ NEUTRAL" };

  return (
    <div className={`p-4 rounded-xl border ${colors[divergence]} shadow-lg h-full`}>
      <div className="flex items-center justify-between mb-3">
        <span className="font-semibold text-white text-sm flex items-center gap-2">
          <span className="text-lg">⚡</span> DXY-BTC Divergence
        </span>
        <span className={`font-bold text-sm ${textColors[divergence]}`}>
          {badges[divergence]}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm mb-3">
        <div className="bg-gray-900/50 rounded-lg p-2">
          <span className="text-gray-500 text-xs block">DXY 7d</span>
          <span className={`font-mono font-bold ${dxy! > 0 ? "text-red-400" : "text-green-400"}`}>
            {dxy! > 0 ? "+" : ""}{dxy?.toFixed(2)}%
          </span>
        </div>
        <div className="bg-gray-900/50 rounded-lg p-2">
          <span className="text-gray-500 text-xs block">BTC 7d</span>
          <span className={`font-mono font-bold ${btc! > 0 ? "text-green-400" : "text-red-400"}`}>
            {btc! > 0 ? "+" : ""}{btc?.toFixed(2)}%
          </span>
        </div>
      </div>
      <p className="text-xs text-gray-400 leading-relaxed">
        {divergence === "bullish" 
          ? "DXY falling + BTC rising = strong risk-on. Dollar weakness fueling crypto rally."
          : divergence === "bearish"
          ? "DXY rising + BTC falling = risk-off. Dollar strength pressuring crypto."
          : "No clear divergence. Markets moving in tandem."}
      </p>
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────
export default function Macro() {
  const [signals, setSignals] = useState<Signal[]>([]);
  const [loading, setLoading] = useState(true);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [updated, setUpdated] = useState("");
  const [filter, setFilter] = useState<"all" | "crypto" | "macro" | "sentiment">("all");
  const [macroSignals, setMacroSignals] = useState<MacroSignal[]>([]);
  const [news, setNews] = useState<NewsItem[]>([]);
  const [newsLoading, setNewsLoading] = useState(true);
  const [newsTab, setNewsTab] = useState<"macro" | "crypto">("macro");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const results = await Promise.all([
        fetchBTCPrice(),
        fetchFundingRate(),
        fetchBTCDominance(),
        fetchGlobalMarketCap(),
        fetchFearGreed(),
      ]);
      setSignals(results);

      // Fetch macro signals
      const macroResults = await Promise.all([
        fetchDXYSignal(),
        fetchYieldSignal(),
        fetchVIXSignal(),
        fetchGoldBTCRatio(),
        fetchDivergenceSignal(),
      ]);
      setMacroSignals(macroResults);
      setUpdated(new Date().toLocaleTimeString());
      setLoading(false);
    };
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const loadNews = async () => {
      setNewsLoading(true);
      const [macroNews, cryptoNews] = await Promise.all([fetchMacroNews(), fetchCryptoMacroNews()]);
      setNews([...macroNews, ...cryptoNews]);
      setNewsLoading(false);
    };
    loadNews();
    const interval = setInterval(loadNews, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const bias = calcBias(signals);
  const filtered = filter === "all" ? signals : signals.filter((s) => s.category === filter);

  return (
    <div className="min-h-screen bg-gray-900 text-white p-4 md:p-6 max-w-7xl mx-auto">

      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-white">🌍 Macro Dashboard</h1>
          <p className="text-gray-400 text-sm mt-1">Daily macro + crypto picture</p>
        </div>
        {updated && <p className="text-xs text-gray-500">Updated {updated} · auto-refreshes every 60s</p>}
      </div>

      {/* Regime banner */}
      {!loading && (
        <div className={`rounded-xl border px-5 py-4 mb-6 flex items-center justify-between flex-wrap gap-4 ${bias.bg}`}>
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-widest mb-1">Today's Regime</p>
            <p className={`text-3xl font-bold ${bias.color}`}>{bias.emoji} {bias.label}</p>
            <p className="text-gray-400 text-sm mt-1">
              {signals.filter(s => s.status === "bullish").length} bullish &nbsp;·&nbsp;
              {signals.filter(s => s.status === "bearish").length} bearish &nbsp;·&nbsp;
              {signals.filter(s => s.status === "neutral").length} neutral
            </p>
          </div>
          {/* Score bar */}
          <div className="w-48">
            <div className="flex justify-between text-xs text-gray-400 mb-1">
              <span>Bear</span><span>Bull</span>
            </div>
            <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${bias.score >= 0 ? "bg-green-500" : "bg-red-500"}`}
                style={{ width: `${Math.min(100, 50 + bias.score / 2)}%`, marginLeft: bias.score < 0 ? `${50 + bias.score / 2}%` : "0" }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Macro Signals Section - Horizontal Scroll */}
      <div className="mb-6">
        <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <span>🎯</span> Macro Signals
        </h2>
        <div className="flex gap-4 overflow-x-auto pb-4 px-1 -mx-1 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
          {macroSignals.map((sig, i) => (
            <div key={i} className="shrink-0 w-72">
              <MacroSignalCard signal={sig} />
            </div>
          ))}
          {/* Divergence Detector as last card */}
          <div className="shrink-0 w-80">
            <DivergenceDetector />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">

        {/* Scorecard */}
        <div className="xl:col-span-1 flex flex-col gap-4">
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">Signal Scorecard</h2>
              <div className="flex gap-1">
                {(["all", "crypto", "sentiment"] as const).map((f) => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    className={`px-2 py-0.5 rounded text-xs transition-colors ${filter === f ? "bg-blue-600 text-white" : "text-gray-400 hover:text-white"}`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>
            {loading ? (
              <p className="text-gray-500 text-sm">Loading signals...</p>
            ) : (
              filtered.map((s, i) => <ScoreRow key={i} signal={s} />)
            )}
          </div>

          {/* Thesis notepad */}
          <ThesisNotepad />
        </div>

        {/* Charts */}
        <div className="xl:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <MiniChart symbol="TVC:DXY"             label="DXY — Dollar Index" />
          <MiniChart symbol="TVC:US10Y"            label="10Y Treasury Yield" />
          <MiniChart symbol="TVC:GOLD"             label="Gold" />
          <MiniChart symbol="BINANCE:BTCUSDT"      label="BTC/USDT" />
          <MiniChart symbol="NASDAQ:MSTR"          label="MSTR" />
          <MiniChart symbol="AMEX:SPY"             label="SPY — S&P 500" />
        </div>
      </div>

      {/* Reference */}
      <div className="mt-6 bg-gray-800 rounded-xl border border-gray-700 p-4">
        <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wide mb-3">Macro Cheat Sheet</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-xs text-gray-400">
          <div>
            <p className="text-white font-medium mb-1">DXY ↑ = Risk-Off</p>
            <p>Strong dollar pressures BTC, gold, and EM assets. Watch for DXY reversals as BTC entry signals.</p>
          </div>
          <div>
            <p className="text-white font-medium mb-1">10Y Yield ↑ = Tighter</p>
            <p>Rising yields = higher discount rate = lower valuations. Risk assets struggle. Falling yields = liquidity.</p>
          </div>
          <div>
            <p className="text-white font-medium mb-1">Funding Rate</p>
            <p>High positive = longs overleveraged, squeeze risk. Negative = shorts piling in, potential squeeze up.</p>
          </div>
          <div>
            <p className="text-white font-medium mb-1">BTC Dominance</p>
            <p>Rising dom = capital flowing to BTC safety. Falling dom = altseason. Stablecoin dom = dry powder.</p>
          </div>
        </div>
      </div>

      {/* News */}
      <div className="mt-6 bg-gray-800 rounded-xl border border-gray-700 p-4">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">📰 Macro News</h2>
          <div className="flex gap-1 bg-gray-900 rounded-lg p-1">
            {(["macro", "crypto"] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setNewsTab(tab)}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors capitalize ${
                  newsTab === tab ? "bg-blue-600 text-white" : "text-gray-400 hover:text-white"
                }`}
              >
                {tab === "macro" ? "🏦 Finance & Macro" : "₿ Crypto"}
              </button>
            ))}
          </div>
        </div>
        <NewsFeed
          items={news.filter((n) => n.category === newsTab)}
          loading={newsLoading}
        />
      </div>
    </div>
  );
}
